/** A1 pre-check and A2 post-check with "Your development" (screens §A1, §A2). A full-bleed page under the shell's top bar. */
import { useEffect, useRef, useState } from "react";
import type { AssessmentAttempt, AssessmentPhase, ErrorBody, ResponseOf } from "@bsim/contracts";
import type { ApiClient } from "../api/client";
import { useRoute, failureOf } from "../api/context";
import type { ApiResult } from "../api/types";
import { GxTopBar } from "../shell/TopBar";
import { navigate, useLocation } from "../shell/history";
import { GxCard, GxErrorBanner, GxWaiting } from "../theme/primitives";
import { CHECK_COPY, DONE_COPY } from "./copy";
import { Development } from "./Development";
import { CheckForm, type CheckExit } from "./Form";
import { contextFromSearch, revealPath } from "./handoff";
import { forgetPreCheckLater, PRE_CHECK_LATER_LINE, rememberPostCheckSent, rememberPreCheckLater } from "./postDone";
import "../shell/shell.css";
import "./checkin.css";

type Load =
  | { readonly status: "loading" }
  | { readonly status: "error"; readonly error: ErrorBody["error"] }
  | { readonly status: "ready"; readonly attempt: AssessmentAttempt; readonly name: string; readonly teamId: string | null; readonly teamRole: string | null; readonly solo: boolean };

/**
 * Starting an attempt is a write (the api records who reached the check, and defers a person who reaches it during a round), so it is a call guarded by a ref, once per
 * cohort, phase and context; who is signed in and the team's feedback are reads through the shared cache.
 */
function useCheck(api: ApiClient, cohortId: string, phase: AssessmentPhase, search: string): Load {
  const me = useRoute("authMe", undefined, { staleTime: Infinity });
  const [started, setStarted] = useState<{ readonly key: string; readonly result: ApiResult<AssessmentAttempt> } | null>(null);
  const key = `${cohortId}.${phase}.${search}`;
  const asked = useRef<string | null>(null);
  useEffect(() => {
    if (asked.current === key) return;
    asked.current = key;
    void api.call("assessmentStart", { cohortId, phase }, { context: contextFromSearch(search) }).then((result) => { if (asked.current === key) setStarted({ key, result }); });
  }, [api, cohortId, phase, search, key]);
  if (started === null || started.key !== key) return me.error !== null ? { status: "error", error: failureOf(me.error) } : { status: "loading" };
  if (!started.result.ok) return { status: "error", error: started.result.error };
  if (me.error !== null) return { status: "error", error: failureOf(me.error) };
  if (me.data === undefined) return { status: "loading" };
  const mine = me.data.principal.memberships.find((m) => m.cohortId === cohortId);
  return { status: "ready", attempt: started.result.value, name: me.data.principal.displayName, teamId: mine?.teamId ?? null, teamRole: mine?.teamRole ?? null, solo: me.data.principal.memberships.some((m) => m.cohortId === cohortId && m.cohortMode === "solo") };
}

function Done({ phase, cohortId, feedback, solo }: { readonly phase: AssessmentPhase; readonly cohortId: string; readonly feedback: ResponseOf<"teamFeedback"> | null; readonly solo: boolean }) {
  return (
    <div className="gx-ci-flow">
      <GxCard className="gx-ci-card gx-ci-done" testId="check.done">
        <span className="gx-ci-tick" aria-hidden="true">✓</span>
        <h1 className="gx-h1">{DONE_COPY}</h1>
        {phase === "pre"
          ? <div><button type="button" className="gx-btn gx-btn--primary gx-btn--lg" data-testid="check.continue" onClick={() => navigate(revealPath(cohortId))} autoFocus>Continue</button></div>
          : null}
      </GxCard>
      {phase === "post" && feedback !== null ? <Development feedback={feedback} solo={solo} /> : null}
    </div>
  );
}

/** The post-check is never forced: "Later" leads back to the player's own page. */
const exitFor = (cohortId: string, solo: boolean): CheckExit => (solo ? { href: `/solo?run=${cohortId}`, label: "Later · Back to your game page" } : { href: `/c/${cohortId}/cockpit`, label: "Later" });

/**
 * The pre-check can be put off by anyone but the CEO, who has to sign: "Do it later" leads back to the cockpit.
 * The api records the deferral itself when a person reaches the pre-check during a round (`preDeferredUserIds`; no route takes a separate deferral); the browser only remembers the banner.
 */
/** A pre-check that will not start: said by whether the team view calls this person to it now (no round being played, or the facilitator called), never from the server's message. */
export const notOpenSentence = (calledToPreCheck: boolean): string =>
  calledToPreCheck ? "Your pre-check is open to you now; reload if this page is stale." : "This check isn't open yet. It waits until the round ends; your facilitator can call you to it.";

export const deferFor = (cohortId: string, teamRole: string | null, solo: boolean, roundBeingPlayed: boolean): CheckExit | undefined =>
  solo || teamRole === null || teamRole === "ceo" ? undefined : { href: `/c/${cohortId}/cockpit`, label: "Do it later", ...(roundBeingPlayed ? { note: PRE_CHECK_LATER_LINE } : {}), onTake: () => rememberPreCheckLater(cohortId) };

export function CheckInPage({ cohortId, phase, api }: { readonly cohortId: string; readonly phase: AssessmentPhase; readonly api: ApiClient }) {
  const { search } = useLocation();
  const load = useCheck(api, cohortId, phase, search);
  const [submitted, setSubmitted] = useState(false);
  const ready = load.status === "ready" ? load : null;
  const isDone = submitted || ready?.attempt.submittedAt != null;
  useEffect(() => { if (phase === "post" && isDone) rememberPostCheckSent(cohortId); }, [phase, isDone, cohortId]);
  // "Waits until this round ends" is true only while a round is being played; before round 1 there is no round to wait for.
  const me = useRoute("authMe", undefined, { staleTime: Infinity });
  const teamId = ready?.teamId ?? me.data?.principal.memberships.find((m) => m.cohortId === cohortId)?.teamId ?? null;
  const teamQuery = useRoute("teamState", phase === "pre" && teamId !== null ? { teamId } : null);
  // The line "it waits until the round ends" is for a person who is not called: once the facilitator has called them in, the page and the banner agree that it is open to them.
  const roundBeingPlayed = teamQuery.data?.me.calledToPreCheck !== true && !["draft", "released", "resolved", undefined].includes(teamQuery.data?.round?.state);
  const feedback = useRoute("teamFeedback", phase === "post" && isDone && ready?.teamId != null ? { teamId: ready.teamId } : null).data ?? null;

  return (
    <div className="gx-ci-page" data-testid={`check.${phase}`}>
      <GxTopBar bankName={CHECK_COPY[phase].title.replace(/\.$/, "")} subline={ready === null ? "" : `About ${ready.attempt.timeBudgetMinutes} minutes`} role={null} name={ready?.name ?? ""} clock={null} />
      <main className="gx-ci-main">
        {load.status === "loading" ? <div className="gx-ci-flow"><GxWaiting what="your check-in" height={220} /></div> : null}
        {load.status === "error" ? <div className="gx-ci-flow"><GxErrorBanner error={load.error} {...(load.error.code === "ASSESSMENT_NOT_OPEN" ? { message: notOpenSentence(teamQuery.data?.me.calledToPreCheck === true) } : {})} /></div> : null}
        {ready === null ? null : isDone
          ? <Done phase={phase} cohortId={cohortId} feedback={feedback} solo={ready.solo} />
          : <CheckForm attempt={ready.attempt} api={api} onSubmitted={() => { if (phase === "post") rememberPostCheckSent(cohortId); else forgetPreCheckLater(cohortId); setSubmitted(true); }} later={phase === "post" ? exitFor(cohortId, ready.solo) : deferFor(cohortId, ready.teamRole, ready.solo, roundBeingPlayed)} solo={ready.solo} />}
      </main>
    </div>
  );
}
